import React from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { TrendingUp, Users, Clock, ArrowRight, Check } from 'lucide-react';

const HIGHLIGHTS = [
    { icon: TrendingUp, label: 'Smart matches', sub: 'Ranked to your profile' },
    { icon: Users, label: 'Warm intros', sub: 'Referrals & signals' },
    { icon: Clock, label: 'Daily tasks', sub: 'A plan, every day' },
];

const CompleteStep = ({ onNext }) => {
    const content = (
        <>
            {/* Backdrop */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="fixed inset-0 z-[99998] backdrop-blur-md"
                style={{ background: 'radial-gradient(120% 90% at 50% 0%, rgba(63,87,224,.12), rgba(15,23,42,.30) 100%)' }}
            />

            <div className="fixed inset-0 z-[99999] flex items-center justify-center pointer-events-none p-4 font-dmsans">
                <motion.div
                    initial={{ opacity: 0, scale: 0.94, y: 24 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.94, y: 24 }}
                    transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
                    className="relative w-[440px] max-w-full bg-white rounded-[24px] border border-[#E6E8EE] pointer-events-auto overflow-hidden"
                    style={{ boxShadow: '0 30px 80px rgba(15,23,42,.22)' }}
                >
                    {/* Soft gradient wash at the top of the card */}
                    <div aria-hidden="true" className="absolute inset-x-0 top-0 h-40 pointer-events-none" style={{ background: 'radial-gradient(100% 100% at 50% 0%, #EEF1FE 0%, transparent 70%)' }} />

                    <div className="relative px-9 pt-10 pb-8 text-center">
                        {/* Success badge with a pulsing halo */}
                        <motion.div
                            initial={{ scale: 0, rotate: -25 }}
                            animate={{ scale: 1, rotate: 0 }}
                            transition={{ delay: 0.1, type: 'spring', stiffness: 220, damping: 15 }}
                            className="relative mx-auto mb-6 grid place-items-center"
                            style={{ width: 88, height: 88 }}
                        >
                            <span aria-hidden="true" className="absolute inset-0 rounded-full animate-ping" style={{ background: 'rgba(16,185,129,.18)' }} />
                            <span className="relative grid place-items-center rounded-full" style={{ width: 72, height: 72, background: 'linear-gradient(135deg,#10B981,#059669)', boxShadow: '0 12px 30px rgba(16,185,129,.40)' }}>
                                <Check size={34} strokeWidth={3} className="text-white" />
                            </span>
                        </motion.div>

                        <h2 className="text-[24px] font-bold tracking-tight text-[#0F172A]">You're all set!</h2>
                        <p className="mt-2 text-[13.5px] text-[#64748B] max-w-[300px] mx-auto leading-relaxed">
                            Your workspace is ready. Let's find you better-matched jobs and warm ways in.
                        </p>

                        {/* Value highlights */}
                        <div className="mt-7 grid grid-cols-3 gap-2.5">
                            {HIGHLIGHTS.map(({ icon: Icon, label, sub }, i) => (
                                <motion.div
                                    key={label}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.22 + i * 0.07, duration: 0.3 }}
                                    className="rounded-2xl border border-[#EEF0F4] bg-[#FAFBFD] px-2.5 py-3.5 text-center"
                                >
                                    <span className="mx-auto mb-2 grid place-items-center rounded-xl" style={{ width: 34, height: 34, background: 'linear-gradient(135deg,rgba(63,87,224,.12),rgba(139,92,246,.12))' }}>
                                        <Icon size={17} style={{ color: '#3F57E0' }} />
                                    </span>
                                    <div className="text-[11.5px] font-semibold text-[#0F172A] leading-tight">{label}</div>
                                    <div className="mt-0.5 text-[10px] text-[#94A3B8] leading-tight">{sub}</div>
                                </motion.div>
                            ))}
                        </div>

                        {/* Single CTA — no skip */}
                        <motion.button
                            type="button"
                            onClick={onNext}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.45, duration: 0.3 }}
                            whileHover={{ scale: 1.015 }}
                            whileTap={{ scale: 0.985 }}
                            className="group mt-7 flex w-full items-center justify-center gap-2 rounded-2xl px-5 py-3.5 text-[14px] font-semibold text-white"
                            style={{ background: 'linear-gradient(135deg,#3F57E0,#8B5CF6)', boxShadow: '0 12px 26px rgba(63,87,224,.36)' }}
                        >
                            Start my job search
                            <ArrowRight size={17} className="transition-transform group-hover:translate-x-0.5" />
                        </motion.button>
                    </div>
                </motion.div>
            </div>
        </>
    );

    return typeof document !== 'undefined'
        ? createPortal(content, document.body)
        : null;
};

export default CompleteStep;
